/* import __COLOCATED_TEMPLATE__ from './fin-phone-behavior.hbs'; */
/* RESPONSIBLE TEAM: team-ai-agent */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import type IntlService from 'embercom/services/intl';
import type Store from '@ember-data/store';
import { action } from '@ember/object';
import type FinVoiceConfigService from 'embercom/services/fin-voice-config-service';
import {
  type FinVoiceVoice,
  DEFAULT_VOICE,
  FIN_VOICE_11LABS_VOICES,
} from 'embercom/objects/ai-agent/fin-voice/fin-voice-voice-options';
import {
  DEFAULT_VOICE_LANGUAGE,
  type FinVoiceVoiceLanguage,
} from 'embercom/objects/ai-agent/fin-voice/fin-voice-language-options';
import type FinVoiceNumberConfiguration from 'embercom/models/fin-voice/number-configuration';

interface Signature {
  Element: HTMLDivElement;
  Args: {
    accordion: any;
    ruleset: $TSFixMe;
    phoneNumber?: string;
    numberConfiguration?: FinVoiceNumberConfiguration;
    updateFinBehavior: (
      voice?: FinVoiceVoice,
      greeting?: string,
      greetingOutOfOfficeHours?: string,
      language?: FinVoiceVoiceLanguage,
    ) => Promise<void>;
  };
}

export default class FinPhoneBehavior extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare store: Store;
  @service declare finVoiceConfigService: FinVoiceConfigService;
  @service declare notificationsService: $TSFixMe;

  @tracked voice?: FinVoiceVoice;
  @tracked selectedLanguage: FinVoiceVoiceLanguage = DEFAULT_VOICE_LANGUAGE;
  @tracked greeting = '';
  @tracked greetingOutOfOfficeHours = '';
  @tracked sameGreetingForOutOfOfficeHours = false;

  constructor(owner: unknown, args: Signature['Args']) {
    super(owner, args);

    this.resetFinBehavior();
  }

  get savingDisabled() {
    return Boolean(
      !this.voice ||
        !this.greeting ||
        !this.args.phoneNumber ||
        this.numberConfiguration?.isSaving ||
        (!this.sameGreetingForOutOfOfficeHours && !this.greetingOutOfOfficeHours),
    );
  }

  get errorMessage() {
    if (!this.args.phoneNumber) {
      return this.intl.t(
        'operator.fin.setup.tabs.setup-and-go-live.phone.fin-behavior.no-phone-numbers',
      );
    }

    if (!this.voice) {
      return this.intl.t(
        'operator.fin.setup.tabs.setup-and-go-live.phone.fin-behavior.error-no-voice',
      );
    }

    if (!this.greeting) {
      return this.intl.t(
        'operator.fin.setup.tabs.setup-and-go-live.phone.fin-behavior.error-no-greeting',
      );
    }

    if (!this.sameGreetingForOutOfOfficeHours && !this.greetingOutOfOfficeHours) {
      return this.intl.t(
        'operator.fin.setup.tabs.setup-and-go-live.phone.fin-behavior.error-no-greeting-out-of-office-hours',
      );
    }

    return undefined;
  }

  get numberConfiguration() {
    return this.args.numberConfiguration;
  }

  get savedVoice() {
    let savedVoice = this.numberConfiguration?.voice;

    if (!savedVoice || !FIN_VOICE_11LABS_VOICES.find((voice) => voice.value === savedVoice)) {
      return undefined;
    }

    return savedVoice;
  }

  get savedGreeting() {
    return this.numberConfiguration?.greeting;
  }

  get savedGreetingOutOfOfficeHours() {
    return this.numberConfiguration?.greetingOutOfOfficeHours;
  }

  get activeVoiceLabel() {
    if (this.savedVoice) {
      let voiceData = FIN_VOICE_11LABS_VOICES.find((voice) => voice.value === this.savedVoice);
      return voiceData?.text || '';
    }

    return '';
  }

  @action
  resetFinBehavior() {
    // Get language from phone number, fallback to default
    let phoneNumber = this.args.phoneNumber;
    let phoneNumberData = phoneNumber
      ? this.finVoiceConfigService.phoneNumbers[phoneNumber]
      : undefined;
    this.selectedLanguage = (phoneNumberData?.locale ||
      DEFAULT_VOICE_LANGUAGE) as FinVoiceVoiceLanguage;

    this.voice = this.savedVoice || DEFAULT_VOICE;
    this.greeting = this.savedGreeting || '';
    this.greetingOutOfOfficeHours = this.savedGreetingOutOfOfficeHours || '';
    this.sameGreetingForOutOfOfficeHours = !this.greetingOutOfOfficeHours;
  }

  @action
  onVoiceLanguageChange(voice: FinVoiceVoice, language: FinVoiceVoiceLanguage) {
    this.voice = voice;
    this.selectedLanguage = language;
  }

  @action
  updateGreeting(event: Event) {
    let target = event.target as HTMLTextAreaElement;
    this.greeting = target.value;
  }

  @action
  updateGreetingOutOfOfficeHours(event: Event) {
    let target = event.target as HTMLTextAreaElement;
    this.greetingOutOfOfficeHours = target.value;
  }

  @action
  toggleUseGreetingOutOfOfficeHours() {
    this.sameGreetingForOutOfOfficeHours = !this.sameGreetingForOutOfOfficeHours;

    if (this.sameGreetingForOutOfOfficeHours) {
      this.greetingOutOfOfficeHours = '';
    }
  }

  @action
  async updateFinBehavior() {
    if (
      (!this.voice && !this.greeting) ||
      (!this.sameGreetingForOutOfOfficeHours && !this.greetingOutOfOfficeHours)
    ) {
      return;
    }

    await this.args.updateFinBehavior(
      this.voice,
      this.greeting,
      this.greetingOutOfOfficeHours,
      this.selectedLanguage,
    );
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Operator::Fin::Setup::Sections::NumberConfiguration::FinPhoneBehavior': typeof FinPhoneBehavior;
  }
}
